/* Cohérence cardiaque — DailyLama
   Maquette : Claude Design « Coherence cardiaque.dc.html ».

   Deux couches : creerSession() = logique pure (horloges et stockage injectés),
   monter() = branchement DOM. La première est testable en millisecondes.

   Deux horloges distinctes et c'est volontaire :
   - `horloge` est monotone (performance.now) et sert à TOUTE la mesure de séance ;
     elle ne recule pas si l'utilisateur change l'heure système.
   - `calendrier` est l'heure murale (Date.now) et ne sert qu'à la série de jours.
*/
(function (global) {
  'use strict';

  var INSPIRE_MS = 5000;
  var EXPIRE_MS = 5000;
  var CYCLE_MS = INSPIRE_MS + EXPIRE_MS;
  var DUREES = [180, 300, 600];
  var DUREE_DEFAUT = 300;
  var PREPARATION_MS = 3000;
  var CLE = 'dailylama.cardiacCoherence.v1';

  function deuxChiffres(n) { return n < 10 ? '0' + n : String(n); }

  function jourCalendaire(ms) {
    var d = new Date(ms);
    return d.getFullYear() + '-' + deuxChiffres(d.getMonth() + 1) + '-' + deuxChiffres(d.getDate());
  }

  function veille(jour) {
    var p = jour.split('-');
    var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
    d.setDate(d.getDate() - 1);
    return d.getFullYear() + '-' + deuxChiffres(d.getMonth() + 1) + '-' + deuxChiffres(d.getDate());
  }

  var VIDE = {
    lastCompletedDate: null, currentStreak: 0, totalCompleted: 0,
    preferredDuration: DUREE_DEFAUT, soundEnabled: false, hideTimer: false
  };

  /* Stockage tolérant : navigation privée, quota plein, JSON corrompu, cookies bloqués. */
  function coffreSur(brut) {
    function lire() {
      var d = {};
      try {
        var s = brut ? brut.getItem(CLE) : null;
        d = s ? JSON.parse(s) : {};
        if (!d || typeof d !== 'object' || Array.isArray(d)) d = {};
      } catch (e) { d = {}; }
      var out = {};
      for (var k in VIDE) {
        out[k] = Object.prototype.hasOwnProperty.call(d, k) ? d[k] : VIDE[k];
      }
      if (DUREES.indexOf(out.preferredDuration) < 0) out.preferredDuration = DUREE_DEFAUT;
      if (typeof out.currentStreak !== 'number' || out.currentStreak < 0) out.currentStreak = 0;
      if (typeof out.totalCompleted !== 'number' || out.totalCompleted < 0) out.totalCompleted = 0;
      out.soundEnabled = out.soundEnabled === true;
      out.hideTimer = out.hideTimer === true;
      if (typeof out.lastCompletedDate !== 'string') out.lastCompletedDate = null;
      return out;
    }
    return {
      lire: lire,
      fusionner: function (partiel) {
        var d = lire();
        for (var k in partiel) d[k] = partiel[k];
        try { if (brut) brut.setItem(CLE, JSON.stringify(d)); } catch (e) {}
        return d;
      }
    };
  }

  function creerSession(options) {
    options = options || {};
    var horloge = options.horloge || function () {
      return (global.performance && global.performance.now) ? global.performance.now() : Date.now();
    };
    var calendrier = options.calendrier || function () { return Date.now(); };
    var coffre = coffreSur(options.stockage);
    var emettre = options.emettre || function () {};

    var prefs = coffre.lire();
    var etat = 'idle';
    var duree = DUREES.indexOf(options.duree) >= 0 ? options.duree : prefs.preferredDuration;
    var finPreparation = null;
    var finPrevue = null;
    var resteEnPause = null;
    var complete = false;
    var son = prefs.soundEnabled;
    var tempsMasque = prefs.hideTimer;
    var calme = options.calme === true;

    function restantMs() {
      if (etat === 'idle' || etat === 'preparing') return duree * 1000;
      if (etat === 'paused') return resteEnPause;
      if (etat === 'completed') return 0;
      return Math.max(0, finPrevue - horloge());
    }

    function ecouleMs() { return duree * 1000 - restantMs(); }

    var api = {
      etat: function () { return etat; },

      /* Vocabulaire détaillé du cahier des charges, dérivé plutôt que stocké :
         un état de plus serait un état de plus à maintenir cohérent. */
      etatDetaille: function () {
        if (etat !== 'running') return etat;
        return this.phase().nom === 'inspire' ? 'running_inhale' : 'running_exhale';
      },

      duree: function () { return duree; },
      calme: function () { return calme; },
      sonActif: function () { return son; },
      tempsMasque: function () { return tempsMasque; },
      preferences: function () { return coffre.lire(); },

      choisirDuree: function (n) {
        if (etat !== 'idle' && etat !== 'completed') return false;   // jamais en pleine séance
        if (DUREES.indexOf(n) < 0) return false;
        if (n === duree) return false;
        duree = n;
        coffre.fusionner({ preferredDuration: n });
        emettre('cardiac_coherence_duration_selected', { duration_seconds: n });
        return true;
      },

      /* Trois secondes de préparation, puis la séance. Un second appel pendant
         la préparation ou la séance est ignoré : pas de minuteurs parallèles. */
      demarrer: function () {
        if (etat !== 'idle' && etat !== 'completed') return false;
        etat = 'preparing';
        complete = false;
        finPreparation = horloge() + PREPARATION_MS;
        finPrevue = null;
        resteEnPause = null;
        emettre('cardiac_coherence_started', { duration_seconds: duree });
        return true;
      },

      compteRebours: function () {
        if (etat !== 'preparing') return 0;
        return Math.max(1, Math.ceil((finPreparation - horloge()) / 1000));
      },

      mettreEnPause: function () {
        if (etat !== 'running') return false;
        resteEnPause = restantMs();
        etat = 'paused';
        emettre('cardiac_coherence_paused', {
          elapsed_seconds: Math.round(ecouleMs() / 1000),
          remaining_seconds: Math.round(resteEnPause / 1000)
        });
        return true;
      },

      reprendre: function () {
        if (etat !== 'paused') return false;
        finPrevue = horloge() + resteEnPause;
        resteEnPause = null;
        etat = 'running';
        emettre('cardiac_coherence_resumed', {});
        return true;
      },

      basculerPause: function () { return this.mettreEnPause() || this.reprendre(); },

      quitter: function () {
        if (etat === 'idle') return false;
        var abandonnee = etat !== 'completed';
        var ecoule = Math.round(ecouleMs() / 1000);
        etat = 'idle';
        finPreparation = null;
        finPrevue = null;
        resteEnPause = null;
        /* Une session abandonnée en moins de dix secondes est un clic d'essai,
           pas un abandon : la compter fausserait le taux. */
        if (abandonnee && ecoule >= 10) {
          emettre('cardiac_coherence_abandoned', {
            elapsed_seconds: ecoule,
            completion_ratio: Math.round((ecoule / duree) * 100) / 100
          });
        }
        return true;
      },

      basculerSon: function () {
        son = !son;
        coffre.fusionner({ soundEnabled: son });
        emettre('cardiac_coherence_sound_toggled', { sound_enabled: son });
        return son;
      },

      basculerTemps: function () {
        tempsMasque = !tempsMasque;
        coffre.fusionner({ hideTimer: tempsMasque });
        emettre('cardiac_coherence_timer_visibility_toggled', { timer_hidden: tempsMasque });
        return tempsMasque;
      },

      basculerCalme: function () { calme = !calme; return calme; },

      restantMs: restantMs,
      restantSecondes: function () { return Math.max(0, Math.ceil(restantMs() / 1000)); },

      horloge: function () {
        var s = this.restantSecondes();
        return Math.floor(s / 60) + ':' + deuxChiffres(s % 60);
      },

      progression: function () {
        if (etat === 'idle' || etat === 'preparing') return 0;
        return Math.min(100, Math.max(0, (ecouleMs() / (duree * 1000)) * 100));
      },

      /* La phase se déduit du temps écoulé : juste au retour d'onglet, sans
         resynchronisation, et exacte au-delà du bridage des minuteurs en arrière-plan. */
      phase: function () {
        if (etat === 'paused') return { nom: 'pause', libelle: 'Session en pause', avancement: 0 };
        if (etat !== 'running') return { nom: 'idle', libelle: '', avancement: 0 };
        var dans = ecouleMs() % CYCLE_MS;
        return dans < INSPIRE_MS
          ? { nom: 'inspire', libelle: 'Inspirez', avancement: dans / INSPIRE_MS }
          : { nom: 'expire', libelle: 'Expirez', avancement: (dans - INSPIRE_MS) / EXPIRE_MS };
      },

      /* Cinq points, un par seconde de la phase en cours. */
      pointsAllumes: function () {
        if (etat !== 'running') return 0;
        var dans = ecouleMs() % INSPIRE_MS;
        return Math.min(5, Math.floor(dans / 1000) + 1);
      },

      /* Appelé à chaque frame. Renvoie l'événement qui vient de se produire, ou null. */
      rafraichir: function () {
        if (etat === 'preparing') {
          if (horloge() < finPreparation) return null;
          etat = 'running';
          finPrevue = horloge() + duree * 1000;
          return 'demarre';
        }
        if (etat !== 'running') return null;
        if (restantMs() > 0) return null;
        if (complete) return null;
        complete = true;
        etat = 'completed';
        this.enregistrerJournee();
        emettre('cardiac_coherence_completed', {
          duration_seconds: duree, completion_ratio: 1
        });
        return 'termine';
      },

      /* Un jour de série = un jour de calendrier, pas une séance : trois séances
         dans l'après-midi ne font pas passer la série de 1 à 3. */
      enregistrerJournee: function () {
        var d = coffre.lire();
        var aujourdhui = jourCalendaire(calendrier());
        var total = d.totalCompleted + 1;
        if (d.lastCompletedDate === aujourdhui) {
          return coffre.fusionner({ totalCompleted: total });
        }
        var serie = d.lastCompletedDate === veille(aujourdhui) ? d.currentStreak + 1 : 1;
        return coffre.fusionner({
          lastCompletedDate: aujourdhui, currentStreak: serie, totalCompleted: total
        });
      },

      libelleCta: function () { return 'Commencer ' + duree / 60 + ' minutes'; },
      libelleRecommencer: function () { return 'Recommencer ' + duree / 60 + ' minutes'; }
    };

    return api;
  }

  global.DailyLamaCoherence = {
    creerSession: creerSession,
    DUREES: DUREES, DUREE_DEFAUT: DUREE_DEFAUT,
    INSPIRE_MS: INSPIRE_MS, EXPIRE_MS: EXPIRE_MS, CYCLE_MS: CYCLE_MS,
    PREPARATION_MS: PREPARATION_MS, CLE: CLE
  };

  /* ------------------------------------------------------------------ */
  /* Couche DOM                                                          */
  /* ------------------------------------------------------------------ */

  var API_DEFAUT = 'https://api.dailylama.shop/v1';
  var apiBase = API_DEFAUT;
  var reseauActif = false;      // posé au montage ; false tant que le backend n'accepte pas ces noms

  function pister(nom, props) {
    props = props || {};
    try {
      global.dataLayer = global.dataLayer || [];
      global.dataLayer.push(Object.assign({ event: nom }, props));
    } catch (e) {}
    try {
      global.dispatchEvent(new CustomEvent('dl:coherence', { detail: { nom: nom, props: props } }));
    } catch (e) {}
    if (!reseauActif) return;
    try {
      global.fetch(apiBase + '/events', {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: nom,
          props: Object.assign({ source_page: location.pathname }, props)
        })
      }).catch(function () {});
    } catch (e) {}
  }

  /* Deux notes douces, au départ et à l'arrivée. Rien entre les deux : un signal
     par seconde ferait exactement le contraire de ce que l'exercice cherche. */
  function creerCarillon() {
    var ctx = null;
    function contexte() {
      var Ctx = global.AudioContext || global.webkitAudioContext;
      if (!Ctx) return null;
      if (!ctx) { try { ctx = new Ctx(); } catch (e) { return null; } }
      if (ctx.state === 'suspended') { try { ctx.resume(); } catch (e) {} }
      return ctx;
    }
    return {
      disponible: function () { return !!(global.AudioContext || global.webkitAudioContext); },
      jouer: function (frequence) {
        var c = contexte();
        if (!c) return false;
        try {
          var o = c.createOscillator();
          var g = c.createGain();
          o.type = 'sine';
          o.frequency.value = frequence;
          g.gain.setValueAtTime(0, c.currentTime);
          g.gain.linearRampToValueAtTime(0.08, c.currentTime + 0.08);
          g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 1.6);
          o.connect(g); g.connect(c.destination);
          o.start(); o.stop(c.currentTime + 1.7);
          return true;
        } catch (e) { return false; }
      },
      debut: function () { return this.jouer(432); },
      fin: function () { return this.jouer(288); },
      fermer: function () {
        var vieux = ctx; ctx = null;
        if (vieux) global.setTimeout(function () { try { vieux.close(); } catch (e) {} }, 2000);
      }
    };
  }

  function monter(racine) {
    if (!racine || racine.dataset.dlcMonte === 'oui') return null;
    racine.dataset.dlcMonte = 'oui';
    apiBase = racine.getAttribute('data-api') || API_DEFAUT;
    reseauActif = racine.getAttribute('data-tracking') === '1';

    var $ = function (s) { return racine.querySelector(s); };
    var $$ = function (s) { return Array.prototype.slice.call(racine.querySelectorAll(s)); };

    var scene = $('[data-dlc-scene]');
    var vues = {
      preparation: $('[data-dlc-vue="preparation"]'),
      seance: $('[data-dlc-vue="seance"]'),
      fin: $('[data-dlc-vue="fin"]')
    };
    var boutonsDuree = $$('[data-dlc-duree]');
    var lancer = $('[data-dlc-lancer]');
    var compteur = $('[data-dlc-compte]');
    var phaseTexte = $('[data-dlc-phase]');
    var points = $$('[data-dlc-point]');
    var mention = $('[data-dlc-mention]');
    var horlogeTexte = $('[data-dlc-horloge]');
    var jauge = $('[data-dlc-jauge]');
    var blocTemps = $('[data-dlc-bloc-temps]');
    var noteMasque = $('[data-dlc-masque]');
    var boutonTemps = $('[data-dlc-temps]');
    var boutonSon = $('[data-dlc-son]');
    var pauses = $$('[data-dlc-pause]');
    var quitters = $$('[data-dlc-quitter]');
    var recommencer = $('[data-dlc-recommencer]');
    var titreFin = $('[data-dlc-titre-fin]');
    var annonce = $('[data-dlc-annonce]');

    var prefereCalme = false;
    try {
      prefereCalme = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {}

    var carillon = creerCarillon();
    var session = global.DailyLamaCoherence.creerSession({
      stockage: (function () { try { return global.localStorage; } catch (e) { return null; } })(),
      calme: prefereCalme,
      emettre: pister
    });

    var boucle = null;
    var declencheur = null;
    var absent = false;
    var texteMention = mention ? mention.textContent : '';

    function dire(texte) { if (annonce) annonce.textContent = texte; }

    function peindreDurees() {
      boutonsDuree.forEach(function (b) {
        var actif = Number(b.dataset.dlcDuree) === session.duree();
        b.setAttribute('aria-checked', actif ? 'true' : 'false');
        b.tabIndex = actif ? 0 : -1;
      });
      if (lancer) lancer.textContent = session.libelleCta();
    }

    function peindreOutils() {
      var masque = session.tempsMasque();
      if (boutonTemps) {
        boutonTemps.setAttribute('aria-pressed', masque ? 'true' : 'false');
        boutonTemps.textContent = masque ? 'Afficher le temps' : 'Masquer le temps';
      }
      if (blocTemps) blocTemps.hidden = masque;
      if (noteMasque) noteMasque.hidden = !masque;

      if (boutonSon) {
        var s = session.sonActif();
        boutonSon.setAttribute('aria-pressed', s ? 'true' : 'false');
        boutonSon.textContent = s ? 'Son activé' : 'Son coupé';
      }

      var enPause = session.etat() === 'paused';
      pauses.forEach(function (b) {
        b.textContent = enPause ? 'Reprendre' : 'Pause';
        b.setAttribute('aria-pressed', enPause ? 'true' : 'false');
      });
      scene.dataset.pause = enPause ? 'oui' : 'non';
      scene.dataset.calme = session.calme() ? 'oui' : 'non';
      if (mention) {
        mention.hidden = !enPause;
        mention.textContent = absent
          ? (mention.getAttribute('data-absence') || texteMention)
          : texteMention;
      }
    }

    function peindreSeance() {
      var p = session.phase();
      if (phaseTexte && phaseTexte.dataset.nom !== p.nom) {
        phaseTexte.dataset.nom = p.nom;
        phaseTexte.textContent = p.libelle;
      }
      var n = session.pointsAllumes();
      points.forEach(function (el, i) { el.dataset.actif = i < n ? 'oui' : 'non'; });
      if (horlogeTexte) horlogeTexte.textContent = session.horloge();
      if (jauge) jauge.style.width = session.progression() + '%';
    }

    function montrer(nom) {
      for (var k in vues) if (vues[k]) vues[k].hidden = (k !== nom);
    }

    function tourner() {
      var evenement = session.rafraichir();
      if (evenement === 'demarre') {
        montrer('seance');
        relancerAnimations();
        if (session.sonActif()) carillon.debut();
        dire('Séance démarrée.');
        var sortie = scene.querySelector('[data-dlc-quitter]');
        if (sortie) sortie.focus();
      } else if (evenement === 'termine') {
        montrerFin();
        return;
      }
      if (session.etat() === 'preparing' && compteur) {
        compteur.textContent = session.compteRebours();
      } else if (session.etat() === 'running') {
        peindreSeance();
      }
      boucle = global.requestAnimationFrame(tourner);
    }

    function arreter() {
      if (boucle) global.cancelAnimationFrame(boucle);
      boucle = null;
    }

    /* Les keyframes CSS sont remises à zéro pour coïncider avec le minuteur. */
    function relancerAnimations() {
      scene.querySelectorAll('[data-dlc-souffle]').forEach(function (el) {
        el.style.animation = 'none';
        void el.offsetWidth;
        el.style.animation = '';
      });
    }

    function surTouche(e) {
      if (e.key === 'Escape') { e.preventDefault(); sortir(); return; }
      if (e.key !== 'Tab') return;
      var vue = vues[session.etat() === 'completed' ? 'fin'
              : session.etat() === 'preparing' ? 'preparation' : 'seance'];
      if (!vue) return;
      var cibles = Array.prototype.slice.call(
        scene.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])')
      ).filter(function (el) { return el.offsetParent !== null && !el.disabled; });
      if (!cibles.length) return;
      var premier = cibles[0], dernier = cibles[cibles.length - 1];
      if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
      else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
    }

    function ouvrir() {
      scene.hidden = false;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', surTouche);
    }

    function fermer() {
      scene.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', surTouche);
      arreter();
      carillon.fermer();
      if (declencheur) { try { declencheur.focus(); } catch (e) {} }
    }

    function montrerFin() {
      arreter();
      if (session.sonActif()) carillon.fin();
      montrer('fin');
      if (recommencer) recommencer.textContent = session.libelleRecommencer();
      dire('Session terminée.');
      if (titreFin) titreFin.focus();
    }

    function sortir() {
      session.quitter();
      absent = false;
      fermer();
      peindreDurees();
    }

    function lancerSeance() {
      if (!session.demarrer()) return;
      absent = false;
      montrer('preparation');
      if (compteur) compteur.textContent = session.compteRebours();
      ouvrir();
      peindreOutils();
      dire('Préparation.');
      arreter();
      boucle = global.requestAnimationFrame(tourner);
    }

    /* ---------- Écoutes ---------- */

    boutonsDuree.forEach(function (b, i) {
      b.addEventListener('click', function () {
        session.choisirDuree(Number(b.dataset.dlcDuree));
        peindreDurees();
      });
      b.addEventListener('keydown', function (e) {
        var pas = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1
                : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
        if (!pas) return;
        e.preventDefault();
        var suivant = boutonsDuree[(i + pas + boutonsDuree.length) % boutonsDuree.length];
        session.choisirDuree(Number(suivant.dataset.dlcDuree));
        peindreDurees();
        suivant.focus();
      });
    });

    if (lancer) lancer.addEventListener('click', function () {
      declencheur = lancer;
      lancerSeance();
    });

    if (recommencer) recommencer.addEventListener('click', lancerSeance);
    quitters.forEach(function (b) { b.addEventListener('click', sortir); });

    pauses.forEach(function (b) {
      b.addEventListener('click', function () {
        if (!session.basculerPause()) return;
        absent = false;
        peindreOutils();
        peindreSeance();
        dire(session.etat() === 'paused' ? 'Session en pause.' : 'Session reprise.');
      });
    });

    if (boutonTemps) boutonTemps.addEventListener('click', function () {
      session.basculerTemps();
      peindreOutils();
    });

    if (boutonSon) boutonSon.addEventListener('click', function () {
      var actif = session.basculerSon();
      /* Si le navigateur refuse l'audio, le bouton ne doit pas prétendre le contraire. */
      if (actif && !carillon.disponible()) {
        session.basculerSon();
        boutonSon.disabled = true;
        boutonSon.textContent = 'Son indisponible';
        return;
      }
      if (actif) carillon.debut();
      peindreOutils();
    });

    /* Quitter l'onglet suspend la séance : le temps passé ailleurs n'est pas du temps respiré. */
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) return;
      if (!session.mettreEnPause()) return;
      absent = true;
      peindreOutils();
      peindreSeance();
    });

    global.addEventListener('pagehide', function () { arreter(); carillon.fermer(); });

    peindreDurees();
    peindreOutils();
    pister('cardiac_coherence_tool_view', { duration_seconds: session.duree() });

    return session;
  }

  global.DailyLamaCoherence.monter = monter;
  global.DailyLamaCoherence.pister = pister;

  if (typeof document !== 'undefined') {
    var demarrage = function () {
      Array.prototype.slice.call(document.querySelectorAll('[data-dlc-racine]')).forEach(monter);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrage);
    else demarrage();
  }
})(typeof window !== 'undefined' ? window : globalThis);
